import { BugReport, Refresh, DeleteSweep } from "@mui/icons-material";
import { Box, Button, Typography, Paper, Alert } from "@mui/material";
import { Component } from "react";
import { Translation } from "react-i18next";

import i18n from "../config/i18n";
import { forceCleanAllData } from "../utils/dataVersion";
import { logger } from "../utils/logger";

import type { ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  reportSent: boolean;
}

/**
 * Error Boundary компонент для обработки критических ошибок
 * Предоставляет возможность отправки отчёта об ошибке и восстановления
 */
export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    reportSent: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, reportSent: false };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (import.meta.env.DEV || import.meta.env.MODE === "development") {
      logger.error("Critical error caught by ErrorBoundary:", error, errorInfo);
    }
    this.setState({ errorInfo });
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  private handleClearDataAndReload = (): void => {
    if (confirm(i18n.t("common:errors.clearDataConfirmation"))) {
      forceCleanAllData();
      window.location.reload();
    }
  };

  private handleReportError = async (): Promise<void> => {
    try {
      const { error, errorInfo } = this.state;
      const errorReport = {
        message: error?.message || i18n.t("common:interface.unknownError"),
        stack: error?.stack || "",
        componentStack: errorInfo?.componentStack || "",
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        url: window.location.href,
      };

      // Send error report to backend (implement your error reporting endpoint)

      if (import.meta.env.DEV || import.meta.env.MODE === "development") {
        logger.debug("Error report:", errorReport);
      }

      // TODO: Implement actual error reporting
      // await fetch('/api/error-report', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(errorReport),
      // });

      this.setState({ reportSent: true });
    } catch (reportError) {
      if (import.meta.env.DEV || import.meta.env.MODE === "development") {
        logger.error("Failed to send error report:", reportError);
      }
    }
  };

  public render(): ReactNode {
    if (this.state.hasError) {
      return (
        <Translation ns="common">
          {(translate): ReactNode => (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "100vh",
                p: 2,
                bgcolor: "background.default",
              }}
            >
              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 2.5, sm: 4 },
                  maxWidth: 600,
                  width: "100%",
                  overflowWrap: "anywhere",
                }}
              >
                <Typography variant="h4" component="h1" gutterBottom>
                  {translate("common:interface.oopsSomethingWentWrong")}
                </Typography>

                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {translate(
                    "common:interface.theApplicationEncounteredAnUnexpectedErrorAndCouldnT",
                  )}
                </Typography>

                {this.state.error && (
                  <Alert severity="error" sx={{ mb: 3, textAlign: "left" }}>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "monospace", fontSize: "0.875rem" }}
                    >
                      {translate("common:interface.error")}:{" "}
                      {this.state.error.message}
                    </Typography>
                  </Alert>
                )}

                {this.state.reportSent && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {translate(
                      "common:interface.thankYouErrorReportHasBeenLogged",
                    )}
                  </Alert>
                )}

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  {translate(
                    "common:interface.pleaseTryReloadingThePageIfTheProblemPersists",
                  )}
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Button
                    variant="contained"
                    onClick={this.handleReload}
                    color="primary"
                    startIcon={<Refresh />}
                    fullWidth
                  >
                    {translate("common:interface.reloadPage")}
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={this.handleReportError}
                    color="info"
                    startIcon={<BugReport />}
                    disabled={this.state.reportSent}
                    fullWidth
                  >
                    {this.state.reportSent
                      ? translate("common:interface.reportSent")
                      : translate("common:interface.reportError")}
                  </Button>

                  <Button
                    variant="text"
                    onClick={this.handleClearDataAndReload}
                    color="warning"
                    size="small"
                    startIcon={<DeleteSweep />}
                    fullWidth
                    sx={{ mt: 1 }}
                  >
                    {translate("common:interface.clearAllDataReloadLastResort")}
                  </Button>
                </Box>

                {process.env.NODE_ENV === "development" &&
                  this.state.errorInfo && (
                    <Box
                      sx={{
                        mt: 3,
                        textAlign: "left",
                        maxHeight: 200,
                        overflow: "auto",
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="error"
                        component="pre"
                        sx={{ fontSize: "0.75rem" }}
                      >
                        {this.state.errorInfo.componentStack}
                      </Typography>
                    </Box>
                  )}
              </Paper>
            </Box>
          )}
        </Translation>
      );
    }

    return this.props.children;
  }
}
